import { useState } from "react";

const SalesChatbotFAQ = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [showAll, setShowAll] = useState<boolean>(false);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const handleShowMore = () => {
    setShowAll(!showAll);
  };

  const faqs = [
    {
      question: "What is AI Chatbot Software?",
      answer:
        "AI Chatbot Software is a program that uses artificial intelligence to automate conversations with users, providing instant responses, customer support, and lead generation.",
    },
    {
      question:
        "What are the benefits of using AI Sales Chatbot Software for businesses?",
      answer: (
        <>
          <p>
            AI Sales Chatbot Software offers a range of benefits, including:
          </p>
          <div className="px-6 flex flex-col gap-2">
            <div>
              <strong>Improved Customer Engagement:</strong> It provides
              personalized interactions based on user activity.
            </div>
            <div>
              <strong>24/7 Availability:</strong> It operates continuously,
              offering assistance at any time.
            </div>
            <div>
              <strong>Automation:</strong> Repetitive tasks like data collection
              and initial interactions are handled efficiently.
            </div>
            <div>
              <strong>Lead Generation:</strong> It converts website visitors
              into leads by gathering and organizing data in CRM systems.
            </div>
            <div>
              <strong>Sales Enablement:</strong> Sales representatives can step
              in to interact directly and close deals faster.
            </div>
            <div>
              <strong>Behavioral Insights:</strong> It tracks visitor activity,
              such as visited pages and geographic location, helping sales teams
              understand customer intent.
            </div>
          </div>
          <p>
            By combining automation, real-time engagement, and actionable
            insights, the chatbot empowers businesses to improve customer
            experience and drive conversions.
          </p>
        </>
      ),
    },
    {
      question: "How does AI Sales Chatbot Software improve customer support?",
      answer: (
        <>
          <p>AI Sales Chatbot Software enhances customer support by:</p>
          <div className="px-6 flex flex-col gap-2">
            <div>
              <strong>Providing Instant Responses:</strong> It handles inquiries
              in real-time, reducing wait times.
            </div>
            <div>
              <strong>Managing Multiple Conversations:</strong> Chatbots can
              address numerous queries simultaneously with consistent accuracy.
            </div>
            <div>
              <strong>Offering 24/7 Support:</strong> Customers can receive
              assistance at any time, ensuring no missed opportunities.
            </div>
            <div>
              <strong>Efficient Issue Resolution:</strong> Simple concerns are
              resolved quickly, while complex cases are seamlessly escalated to
              human agents.
            </div>
            <div>
              <strong>Behavioral Tracking:</strong> It identifies customer
              activities, such as browsing history and location, enabling
              tailored support and proactive engagement.
            </div>
          </div>
          <p>
            This approach ensures quicker resolutions, better personalization,
            and improved overall customer satisfaction while freeing up sales
            teams to focus on high-priority tasks.
          </p>
        </>
      ),
    },
    {
      question:
        "What are the benefits of using AI Chatbot Software?",
      answer:
        "AI chatbots improve customer engagement, automate repetitive tasks, generate leads, increase sales, and provide 24/7 support without human intervention.",
    },
    {
      question: "Is AI Chatbot Software suitable for small businesses?",
      answer:
        "Yes, AI chatbots help small businesses save time, reduce customer support costs, and improve engagement by automating interactions with potential customers.",
    },
    {
      question:
        "How can AI chatbots help with lead generation?",
      answer:
        "AI chatbots qualify leads by asking pre-set questions, collecting contact details, and guiding potential customers through the sales funnel automatically.",
    },
    {
      question: "How secure is AI Sales Chatbot Software?",
      answer:
        "AI Sales Chatbot Software prioritizes data security using encryption, access controls, and compliance with regulations like GDPR to protect sensitive data.",
    },
    
    {
      question: "How much does AI Sales Chatbot Software cost?",
      answer:
        "Costs vary depending on features, user capacity, and subscription plans. Some platforms offer free versions, while others have tiered pricing based on usage.",
    },
    {
      question: "How do I add the chatbot to my website?",
      answer:
        "The chatbot can be added using a unique URL or an embed code. The embed code integrates the chatbot directly into your website for seamless access.",
    },
    {
      question: "How do I choose the best AI Chatbot Software for my business?",
      answer:
        "Consider factors like ease of use, integration options, AI capabilities, customization, pricing, and customer support when selecting an AI chatbot solution.",
    },
  ];

  const displayedFAQs = showAll ? faqs : faqs.slice(0, 3);

  return (
    <div
      className="px-4 sm:px-8 mb-[100px] max-w-5xl mx-auto flex text-gray-700 flex-col gap-12 mt-[100px]"
      itemScope
      itemType="https://schema.org/FAQPage"
    >
      <div className="flex flex-col text-left">
        <p className="sm:text-[30px] text-[25px] font-extrabold text-base-content">
          Frequently Asked Questions
        </p>
      </div>
      <ul className="">
        {faqs.map((faq, index) => (
          <li key={index} className={!showAll && index >= 3 ? "hidden" : ""}>
            <button
              itemScope
              itemProp="mainEntity"
              itemType="https://schema.org/Question"
              className="relative flex flex-col gap-2 w-full py-5 text-[16px] font-semibold text-left md:text-[16px]  faq"
              aria-expanded={openFAQ === index}
              onClick={() => toggleFAQ(index)}
            >
              <div className="flex justify-between items-center w-full">
                <h3 className="flex-1 text-[16px] font-medium" itemProp="name">
                  {faq.question}
                </h3>
                <svg
                  className="flex-shrink-0 w-4 h-4 ml-auto fill-current"
                  viewBox="0 0 16 16"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    y="7"
                    width="16"
                    height="2"
                    rx="1"
                    className={`transform origin-center transition duration-200 ease-out ${
                      openFAQ === index ? "rotate-90" : ""
                    }`}
                  ></rect>
                  <rect
                    y="7"
                    width="16"
                    height="2"
                    rx="1"
                    className="transform origin-center rotate-90 transition duration-200 ease-out"
                  ></rect>
                </svg>
              </div>
              <div
                itemScope
                itemProp="acceptedAnswer"
                itemType="https://schema.org/Answer"
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  openFAQ === index ? "max-h-[500px]" : "max-h-0"
                }`}
              >
                <div className="pb-5 leading-relaxed">
                  <div
                    className="space-y-2 leading-relaxed font-normal text-[16px]"
                    itemProp="text"
                  >
                    {faq.answer}
                  </div>
                </div>
              </div>
            </button>
          </li>
        ))}
      </ul>
      {!showAll && (
        <div className="flex justify-end">
          <button
            onClick={handleShowMore}
            className="cursor-pointer px-3 py-2 rounded-md text-white tracking-wider shadow-xl animate-bounce hover:animate-none"
          >
            <svg
              className="w-5 h-5"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3"
                strokeLinejoin="round"
                strokeLinecap="round"
              ></path>
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default SalesChatbotFAQ;
